import {ActivatedRouteSnapshot} from '@angular/router';

/**
 * RouteOptions handle providing available values, any applied value (specified on the route via matrix params) and
 *  a default value when a value is not provided on the route, or that value is not valid (found within values).
 *
 * Type notes: `unknown` won't work here because when you type RouteOption[], any comparator from subclasses can't be
 *  assigned to `unknown`
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export abstract class RouteOption<Value = any> {
	readonly appliedValue: Value;

	// Must provide implementation
	abstract toValue(value: string): Value | undefined;
	// Must provide implementation
	abstract toString(value?: Value): string;

	protected constructor(
		readonly key: string,
		protected route: ActivatedRouteSnapshot,
		readonly values: Set<Value> | null, // null indicates no constraint on values
		readonly defaultValue: Value
	) {
		this.appliedValue = this.getAppliedValue();
	}

	isDefaultValue(value: Value | undefined): boolean {
		return this.isEqual(value, this.defaultValue);
	}

	isEqual(a: Value | undefined, b: Value | undefined): boolean {
		return this.toString(a) === this.toString(b);
	}

	/**
	 * Get the applied value from the route.
	 * @return {Value}
	 * @private
	 */
	private getAppliedValue(): Value {
		const routeParamValue = this.route.paramMap.get(this.key);
		let value: Value | undefined;
		if (routeParamValue) {
			const routeOptionValue = this.toValue(routeParamValue);
			value = this.values === null
				// If `values` is null, then `appliedValue` is unconstrained
				? routeOptionValue
				// otherwise attempt to fetch from `values` to ensure it is a valid value
				: this.values?.values().find(x => this.toString(x) === routeParamValue);
		}
		// fallback to `defaultValue`
		return value ?? this.defaultValue;
	}

}


